import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-invite-panel.css?inline';
// The role select renders light-DOM markup; inside this shadow root the page stylesheet cannot reach it.
import roleSelectStyles from '../pk-role-select/pk-role-select.host.css?inline';
import { fillId, formatUtc, parseJsonArray } from '../../lib/portal-data.js';
import { PK_ROLES } from '../pk-role-select/pk-role-select.js';
import '../pk-alert/pk-alert.js';
import '../pk-button/pk-button.js';
import '../pk-role-select/pk-role-select.js';
import '../pk-token-reveal/pk-token-reveal.js';

/** One invite (contract `Invite`). */
export interface PkInviteRecord {
    id: string;
    email?: string | null;
    role?: number;
    status?: string;
    createdAtUtc?: string | null;
    expiresAtUtc?: string | null;
}

/**
 * `<pk-invite-panel>` — tenant invites: a create form (email + role), the one-time link of the invite
 * just created, and the pending list with a Revoke button per row.
 *
 * Every action is a plain form POST to the CMS (no fetch). After a create, the server renders the page
 * again with `created-link` set **once**: the link is shown through `<pk-token-reveal>` (copy, hide)
 * and never again — the invitee can also use the emailed link. Only `pending` invites are listed;
 * redeemed / expired / revoked ones are counted underneath.
 *
 * Attributes:
 *   - `invites`       — JSON array of invites (or set the `invites` property).
 *   - `action`        — create POST URL (default `/portal/admin/invites`).
 *   - `revoke-action` — revoke POST URL template, `{id}` replaced (default `/portal/admin/invites/{id}/revoke`).
 *   - `max-role`      — highest role an invite may confer, 0–4 (default 2 = Member; the contract caps
 *                       invites at Member). Pass `min(2, caller level)`.
 *   - `default-role`  — preselected role (default `reader`).
 *   - `created-link`  — the registration URL of the invite just created (shown once).
 *   - `error`         — error text shown above the form.
 *   - `csrf-token` / `csrf-name` — anti-forgery hidden input in every form.
 *
 * Events:
 *   - `pk-invite-create` — `CustomEvent<{ email: string; role: string }>`, cancelable, as the create form submits.
 *   - `pk-invite-revoke` — `CustomEvent<{ id: string }>`, cancelable, as a revoke form submits.
 */
export class PkInvitePanel extends HTMLElement {
    static readonly tagName = 'pk-invite-panel';

    static get observedAttributes(): string[] {
        return ['invites', 'action', 'revoke-action', 'max-role', 'default-role', 'created-link', 'error', 'csrf-token', 'csrf-name'];
    }

    private readonly _root: ShadowRoot;
    private _invites: PkInviteRecord[] | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
    }

    attributeChangedCallback(name: string): void {
        if (name === 'invites') this._invites = null;
        if (this.isConnected) this._render();
    }

    /** The invites (property wins over the attribute once set). */
    get invites(): PkInviteRecord[] {
        return this._invites ?? parseJsonArray<PkInviteRecord>(this.getAttribute('invites'));
    }

    set invites(value: PkInviteRecord[]) {
        this._invites = Array.isArray(value) ? value : [];
        if (this.isConnected) this._render();
    }

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        let proceed = true;
        if (form.classList.contains('create')) {
            const email = (form.querySelector('input[name="email"]') as HTMLInputElement | null)?.value ?? '';
            const role = (form.querySelector('select[name="role"]') as HTMLSelectElement | null)?.value ?? '';
            proceed = this.dispatchEvent(new CustomEvent('pk-invite-create', {
                bubbles: true, composed: true, cancelable: true, detail: { email, role },
            }));
        } else if (form.classList.contains('revoke')) {
            proceed = this.dispatchEvent(new CustomEvent('pk-invite-revoke', {
                bubbles: true, composed: true, cancelable: true, detail: { id: form.dataset.inviteId ?? '' },
            }));
        }
        if (!proceed) e.preventDefault();
    };

    private _csrf(): TemplateResult | string {
        const token = this.getAttribute('csrf-token') ?? '';
        const name = this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
        return token ? html`<input type="hidden" name=${name} .value=${token} />` : '';
    }

    private _maxRole(): number {
        const raw = Number(this.getAttribute('max-role') ?? '2');
        return Number.isFinite(raw) ? Math.min(4, Math.max(0, Math.trunc(raw))) : 2;
    }

    private template(): TemplateResult {
        const all = this.invites;
        const pending = all.filter((i) => (i.status ?? 'pending') === 'pending');
        const others = all.length - pending.length;
        const action = this.getAttribute('action') ?? '/portal/admin/invites';
        const revokeAction = this.getAttribute('revoke-action') ?? '/portal/admin/invites/{id}/revoke';
        const createdLink = this.getAttribute('created-link') ?? '';
        const error = this.getAttribute('error') ?? '';
        const defaultRole = this.getAttribute('default-role') ?? 'reader';

        return html`
            <style>${styles}${roleSelectStyles}</style>

            ${createdLink ? html`
                <pk-token-reveal
                    class="created-link"
                    label="Invite link"
                    warning="Shown once — copy it now. The invitee can also use the link in their email."
                    token=${createdLink}
                ></pk-token-reveal>
            ` : ''}

            ${error ? html`<pk-alert variant="error" class="form-error">${error}</pk-alert>` : ''}

            <form class="create" method="post" action=${action}>
                ${this._csrf()}
                <label class="field">
                    <span>Email</span>
                    <input type="email" name="email" autocomplete="off" placeholder="name@company.com" />
                </label>
                <label class="field">
                    <span>Role</span>
                    <pk-role-select name="role" max-level=${String(this._maxRole())} value=${defaultRole}></pk-role-select>
                </label>
                <pk-button variant="primary" type="submit">Send invite</pk-button>
            </form>

            <h3 class="pending-heading">Pending invites <span class="count">${pending.length}</span></h3>
            ${pending.length === 0
                ? html`<p class="empty">No pending invites.</p>`
                : html`
                    <table>
                        <thead>
                            <tr>
                                <th scope="col">Email</th>
                                <th scope="col">Role</th>
                                <th scope="col">Expires</th>
                                <th scope="col"><span class="visually-hidden">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pending.map((i) => html`
                                <tr data-invite-id=${i.id}>
                                    <td class="email">${i.email || 'Anyone with the link'}</td>
                                    <td class="role">${PK_ROLES[i.role ?? 0]?.label ?? 'Reader'}</td>
                                    <td class="expires" data-iso=${i.expiresAtUtc ?? ''}>${formatUtc(i.expiresAtUtc)}</td>
                                    <td class="actions">
                                        <form class="revoke" method="post" action=${fillId(revokeAction, i.id)} data-invite-id=${i.id}>
                                            ${this._csrf()}
                                            <pk-button variant="secondary" type="submit">Revoke</pk-button>
                                        </form>
                                    </td>
                                </tr>
                            `)}
                        </tbody>
                    </table>
                `}
            ${others > 0 ? html`<p class="others">${others} accepted, expired or revoked.</p>` : ''}
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkInvitePanel.tagName)) {
    customElements.define(PkInvitePanel.tagName, PkInvitePanel);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-invite-panel': PkInvitePanel;
    }
}
